import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createTenderSchema } from "../../createTender/schema/tender-schema";
import { z } from "zod";
import { Form } from "@/app/global-components/form";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import CloseOfferSubmitView from "./tender-close-submit-view";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import useOffer from "@/hooks/useTender";
import { Box, Alert } from "@mui/material";
import { AlertCircle } from "lucide-react";

interface Product {
  item: string;
  quantity: number;
  attachment: File | null;
}

interface TenderEdFormViewProps {
  offer?: Offer | null | undefined;
  mutate: () => void;
}

interface Offer {
  title: string;
  value: number;
  endDate: string;
  openingDate: string;
  type: string;
  status: string;
  categories: string[];
  categoriesIds: string[];
  region: string;
  city: string;
  fileName: string;
  fileDescription: string;
  attachmentName: string;
  attachmentDescription: string;
  receiveDocumentsType: string;
  products: Product[];
  Paylater: string;
  attachmentDeliverDays: number | null;
  attachmentId: string | null;
  companyId: string;
  fileId: File | null;
  id: string | null;
  userId: string;
}

const allowedStatuses = [
  "Planning",
  "Opened",
  "Closed",
  "Accepted",
  "Finished",
  "Payment",
  "PaymentConfirmation",
  "Sending",
  "Receiving",
] as const;

const allowedTypes = ["Tiny", "Huge"] as const;
// const allowedReceiveDocumentsTypes = ["Both", "OneByOne"] as const;
const allowedPaylaterValues = ["Yes", "No"] as const;

// Type Guards
function isValidStatus(
  status: string
): status is (typeof allowedStatuses)[number] {
  return allowedStatuses.includes(status as (typeof allowedStatuses)[number]);
}
function isValidType(type: string): type is (typeof allowedTypes)[number] {
  return allowedTypes.includes(type as (typeof allowedTypes)[number]);
}

// function isValidReceiveDocumentsType(
//   receiveDocumentsType: string
// ): receiveDocumentsType is (typeof allowedReceiveDocumentsTypes)[number] {
//   return allowedReceiveDocumentsTypes.includes(
//     receiveDocumentsType as (typeof allowedReceiveDocumentsTypes)[number]
//   );
// }

function isValidPaylater(
  Paylater: string
): Paylater is (typeof allowedPaylaterValues)[number] {
  return allowedPaylaterValues.includes(
    Paylater as (typeof allowedPaylaterValues)[number]
  );
}

const TenderCloseFormView: React.FC<TenderEdFormViewProps> = ({
  offer,
  mutate,
}) => {
  const { t } = useTranslate(useLocale().locale);
  const { updateOfferd, isSaveingLoading, error } = useOffer();
  const myCompany = useSelector((state: RootState) => state.auth.myCompany);

  const TenderSchema = createTenderSchema(t as (key: string) => string);

  const form = useForm<z.infer<typeof TenderSchema>>({
    resolver: zodResolver(TenderSchema),
    defaultValues: {
      id: null,
      title: "",
      endDate: "",
      openingDate: "",
      type: "Tiny",
      status: null,
      region: "",
      city: "",
      Paylater: "No",
      categoriesIds: [],
      fileId: null,
      products: [],
      companyId: myCompany?.id,
    },
  });

  useEffect(() => {
    if (myCompany?.id) {
      form.setValue("companyId", myCompany.id);
    }
  }, [myCompany, form]);

  useEffect(() => {
    if (offer) {
      const productsWithDefinedAttachments = offer.products.map((product) => ({
        ...product,
        attachment:
          product.attachment &&
          typeof product.attachment === "object" &&
          "url" in product.attachment
            ? null
            : product.attachment,
      }));

      const status = isValidStatus(offer.status) ? offer.status : "Planning";
      const type = isValidType(offer.type) ? offer.type : "Tiny";

      const Paylater = isValidPaylater(offer.Paylater) ? offer.Paylater : "No";

      form.reset({
        ...offer,
        status,

        type,
        Paylater,
        products: productsWithDefinedAttachments,
      });
    }
  }, [offer, form]);

  const handleClose = async (values: z.infer<typeof TenderSchema>) => {
    try {
      values.status = "Closed";
      await updateOfferd({ ...values, id: offer?.id ?? null });
      mutate();
    } catch (err) {
      console.error("Error closing tender:", err);
    }
  };
  return (
    <Form {...form}>
      <form onSubmit={(e) => e.preventDefault()}>
        <Box>
          {error && (
            <Alert
              severity="error"
              icon={<AlertCircle size={20} />}
              sx={{ mb: 2, borderRadius: 2 }}
            >
              {error}
            </Alert>
          )}

          <CloseOfferSubmitView
            disabled={offer?.status === "Closed"}
            isLoading={isSaveingLoading}
            onClick={form.handleSubmit(handleClose)}
          />
        </Box>
      </form>
    </Form>
  );
};

export default TenderCloseFormView;
